import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Header } from "../components/Header";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { GraduationCap, PlayCircle, CheckCircle2, Circle, Download, ShieldCheck } from "lucide-react";

export default function ExamIntro() {
  const { courseId } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();

  const [meta, setMeta] = useState(null);
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getCourse(courseId).then(setCourse).catch(() => {});
    api.examMeta(courseId).then(setMeta).catch((e) => setError(e.message));
  }, [courseId]);

  async function startAttempt() {
    setBusy(true);
    setError(null);
    try {
      const a = await api.startAttempt(courseId);
      navigate(`/courses/${courseId}/exam/attempt/${a.attempt_id}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!meta) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="mx-auto max-w-3xl px-6 py-24">
          <div className="h-64 rounded-3xl bg-white border-2 border-black/5 animate-pulse" />
        </div>
      </div>
    );
  }

  const eligibility = meta.eligibility;
  const missingModuleIds = new Set(eligibility.missing_module_ids);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="exam-intro-page">
      <Header />
      <div className="mx-auto max-w-3xl px-6 md:px-10 py-12 md:py-16">
        <Link
          to={`/courses/${courseId}`}
          className="text-sm font-bold text-black/60 hover:text-[#4361EE]"
          data-testid="exam-back"
        >
          ← {t("exam.backToCourse")}
        </Link>

        <div className="mt-4 flex items-start gap-4">
          <div className="h-16 w-16 rounded-2xl bg-[#EDE9FE] text-[#4C1D95] grid place-items-center">
            <GraduationCap className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight" data-testid="exam-title">
              {t("exam.title")}
            </h1>
            {course && (
              <p className="mt-1 text-lg text-black/70 font-semibold">
                {course.title[lang] || course.title.en}
              </p>
            )}
            <p className="mt-2 text-black/70">{t("exam.subtitle")}</p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-3 gap-3">
          <Fact label={t("exam.questions")} value={meta.total_questions} />
          <Fact label={t("exam.threshold")} value={`${meta.threshold_percent}%`} />
          <Fact label="≈ min" value={meta.estimated_minutes} />
        </div>

        {meta.certificate && (
          <div
            className="mt-8 rounded-3xl bg-gradient-to-br from-[#DCFCE7] to-[#DFF6FD] border-2 border-[#166534]/20 p-6"
            data-testid="exam-existing-cert"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-[#166534] text-white font-black px-3 py-1 text-xs uppercase tracking-wider">
              <CheckCircle2 className="h-3.5 w-3.5" /> {t("exam.passed")}
            </div>
            <p className="mt-3 font-extrabold text-[#1A1A1A]">
              {t("exam.score")}: {meta.certificate.score_percent}%
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${meta.certificate.pdf_url}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] text-white font-bold px-5 py-2.5 text-sm shadow-[0_3px_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_#000] transition"
                data-testid="cert-download"
              >
                <Download className="h-4 w-4" /> {t("exam.downloadCert")}
              </a>
              <Link
                to={`/verify/${meta.certificate.certificate_id}`}
                className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-black/10 hover:border-black/30 font-bold px-5 py-2.5 text-sm transition"
                data-testid="cert-verify-link"
              >
                <ShieldCheck className="h-4 w-4" /> {t("exam.shareVerify")}
              </Link>
            </div>
          </div>
        )}

        {eligibility.eligible ? (
          <button
            onClick={startAttempt}
            disabled={busy}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#4361EE] hover:bg-[#4361EE]/90 disabled:opacity-60 text-white font-bold px-7 py-4 shadow-[0_4px_0_#2A3EB1] hover:translate-y-[1px] hover:shadow-[0_3px_0_#2A3EB1] transition"
            data-testid="exam-start-btn"
          >
            <PlayCircle className="h-5 w-5" /> {meta.active_attempt_id ? t("exam.resumeCta") : (meta.certificate ? t("exam.retakeCta") : t("exam.startCta"))}
          </button>
        ) : (
          <div className="mt-10 rounded-3xl bg-white border-2 border-black/5 p-6" data-testid="exam-prereq">
            <h2 className="text-2xl font-black">{t("exam.prereqTitle")}</h2>
            <p className="mt-1 text-black/70 font-medium">{t("exam.prereqBody")}</p>
            {course && (
              <ul className="mt-4 space-y-2">
                {course.modules
                  .filter((m) => m.type !== "exam")
                  .map((m) => {
                    const missing = missingModuleIds.has(m.module_id);
                    return (
                      <li
                        key={m.module_id}
                        className={`flex items-center gap-3 text-sm ${missing ? "text-black/80" : "text-black/40 line-through"}`}
                        data-testid={`prereq-${m.order}`}
                      >
                        {missing ? (
                          <Circle className="h-4 w-4 flex-shrink-0" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4 text-[#166534] flex-shrink-0" />
                        )}
                        <span className="font-bold">{m.order}.</span>
                        <span className="truncate">{m.title[lang] || m.title.en}</span>
                      </li>
                    );
                  })}
              </ul>
            )}
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-2xl bg-[#FEE2E2] text-[#991B1B] px-4 py-3 text-sm font-semibold" data-testid="exam-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div className="rounded-2xl bg-white border-2 border-black/5 p-4 text-center">
      <div className="text-3xl font-black text-[#1A1A1A]">{value}</div>
      <div className="text-[10px] font-black uppercase tracking-wider text-black/50 mt-1">{label}</div>
    </div>
  );
}
